import chargeTransactionApi from 'api/chargeTransactionApi';
import AppConstants from 'general/constants/AppConstants';
import Utils from 'general/utils/Utils';
import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';

const PEAK_HOURS_SLOT_TYPES = {
  peakHours: 'peakMinutes',
  normalHours: 'normalMinutes',
  offPeakHours: 'offPeakMinutes',
};

const applyTimeOnDay = (dayMoment, timeStr) => {
  const [hours = 0, minutes = 0, seconds = 0] = String(timeStr)
    .split(':')
    .map((part) => _.toNumber(part) || 0);
  return dayMoment.clone().hours(hours).minutes(minutes).seconds(seconds).milliseconds(0);
};

const getSlotEndMoment = (dayMoment, timeStr) => {
  if (String(timeStr) === '23:59:59') {
    return dayMoment.clone().add(1, 'day').startOf('day');
  }
  return applyTimeOnDay(dayMoment, timeStr);
};

const reconcileRoundedMinutes = (minutesByType, totalMinutes) => {
  const target = Math.round(_.toNumber(totalMinutes) || 0);
  if (target <= 0) {
    return { peakMinutes: 0, normalMinutes: 0, offPeakMinutes: 0 };
  }

  const rounded = {
    peakMinutes: Math.round(minutesByType.peakMinutes || 0),
    normalMinutes: Math.round(minutesByType.normalMinutes || 0),
    offPeakMinutes: Math.round(minutesByType.offPeakMinutes || 0),
  };
  let remaining = target - (rounded.peakMinutes + rounded.normalMinutes + rounded.offPeakMinutes);
  if (remaining === 0) {
    return rounded;
  }

  const priorities = [
    { key: 'peakMinutes', weight: minutesByType.peakMinutes || 0 },
    { key: 'normalMinutes', weight: minutesByType.normalMinutes || 0 },
    { key: 'offPeakMinutes', weight: minutesByType.offPeakMinutes || 0 },
  ].sort((a, b) => b.weight - a.weight);

  let idx = 0;
  while (remaining !== 0 && priorities.length > 0) {
    const key = priorities[idx].key;
    if (remaining > 0) {
      rounded[key] += 1;
      remaining -= 1;
    } else if (rounded[key] > 0) {
      rounded[key] -= 1;
      remaining += 1;
    }
    idx = (idx + 1) % priorities.length;
  }

  return rounded;
};

const ChargeTransactionHelper = {
  // ---- methods ----
  // lay chi tiet phien sac
  callApiGetChargeTransactionDetail: async (id) => {
    const res = await chargeTransactionApi.getChargeTransactionDetail(id);
    const { result, chargeTransaction } = res;
    if (result === 'success' && chargeTransaction) {
      return chargeTransaction;
    }
    return {};
  },

  // lay ds phien sac
  callApiGetChargeTransactionList: async (params) => {
    const res = await chargeTransactionApi.getChargeTransactionList(params);
    const { result, chargeTransactions, total } = res;
    if (result === 'success') {
      return { chargeTransactions, total };
    }
    return {};
  },

  // lay mau trang thai phien sac
  getChargeTransactionStatusColor: (status) => {
    switch (status) {
      case AppConstants.chargeTransactionStatus.cancelled:
        return 'danger';
      case AppConstants.chargeTransactionStatus.failed:
        return 'danger';
      case AppConstants.chargeTransactionStatus.charging:
        return 'success';
      case AppConstants.chargeTransactionStatus.finished:
        return 'gray-600';
      case AppConstants.chargeTransactionStatus.finishing:
        return 'success';
      case AppConstants.chargeTransactionStatus.preparing:
        return 'warning';
      case AppConstants.chargeTransactionStatus.initiating:
        return 'primary';
      case AppConstants.chargeTransactionStatus.reserved:
        return 'gray-600';
      default:
        return 'danger';
    }
  },

  // kiem tra dang sac?
  isCharging: (status) => {
    return status == AppConstants.chargeTransactionStatus.charging;
  },
  isFinishing: (status) => {
    return status == AppConstants.chargeTransactionStatus.finishing;
  },
  isFinished: (status) => {
    return status == AppConstants.chargeTransactionStatus.finished;
  },
  isCancelled: (status) => {
    return status == AppConstants.chargeTransactionStatus.cancelled;
  },
  isFailed: (status) => {
    return status == AppConstants.chargeTransactionStatus.failed;
  },
  isReserved: (status) => {
    return status == AppConstants.chargeTransactionStatus.reserved;
  },

  getAmount: (item) => {
    const payments = item?.payments ?? [];
    const estimatedCost = item?.estimatedCost ?? 0;
    const currency = item?.currency ?? '';

    if (!_.isEmpty(payments)) {
      // return Utils.formatNumber(_.first(payments)?.amount) + ' ' + _.first(payments)?.currency;
      return Utils.formatNumber(_.first(payments)?.amount);
    }
    // return Utils.formatNumber(estimatedCost) + ' ' + currency;
    return Utils.formatNumber(estimatedCost);
  },

  // filter options
  filterStatusOptions: () => {
    return [
      { name: t('all'), value: '' },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.cancelled),
        value: AppConstants.chargeTransactionStatus.cancelled,
      },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.charging),
        value: AppConstants.chargeTransactionStatus.charging,
      },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.finished),
        value: AppConstants.chargeTransactionStatus.finished,
      },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.finishing),
        value: AppConstants.chargeTransactionStatus.finishing,
      },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.initiating),
        value: AppConstants.chargeTransactionStatus.initiating,
      },
      {
        name: t('chargeTransactionStatus_' + AppConstants.chargeTransactionStatus.preparing),
        value: AppConstants.chargeTransactionStatus.preparing,
      },
    ];
  },

  parsePeakHoursConfig: (peakHoursConfig) => {
    if (!peakHoursConfig) {
      return [];
    }
    if (_.isArray(peakHoursConfig)) {
      return peakHoursConfig;
    }
    if (_.isString(peakHoursConfig)) {
      try {
        const parsed = JSON.parse(peakHoursConfig);
        return _.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  // Tinh so phut sac theo tung khung gio tu thoi gian bat dau/ket thuc va peakHoursConfig
  calcChargingMinutesByPeakHoursConfig: (startTime, endTime, peakHoursConfig) => {
    const start = moment(startTime);
    const end = moment(endTime);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return null;
    }

    const config = ChargeTransactionHelper.parsePeakHoursConfig(peakHoursConfig);
    if (_.isEmpty(config)) {
      return null;
    }

    const configByDay = _.keyBy(config, (item) => _.toNumber(item?.dayOfWeek));
    const rawMinutes = { peakMinutes: 0, normalMinutes: 0, offPeakMinutes: 0 };

    let dayCursor = start.clone().startOf('day');
    const lastDay = end.clone().startOf('day');

    while (dayCursor.isSameOrBefore(lastDay)) {
      const dayConfig = configByDay[dayCursor.day()];
      const timeSlots = dayConfig?.timeSlots ?? [];

      timeSlots.forEach((slot) => {
        const slotType = PEAK_HOURS_SLOT_TYPES[slot?.type];
        if (!slotType || !slot?.from || !slot?.to) {
          return;
        }

        const slotStart = applyTimeOnDay(dayCursor, slot.from);
        const slotEnd = getSlotEndMoment(dayCursor, slot.to);
        const overlapStart = moment.max(start, slotStart);
        const overlapEnd = moment.min(end, slotEnd);

        if (overlapEnd.isAfter(overlapStart)) {
          rawMinutes[slotType] += overlapEnd.diff(overlapStart, 'minutes', true);
        }
      });

      dayCursor = dayCursor.add(1, 'day');
    }

    const totalMinutes = end.diff(start, 'minutes', true);
    return reconcileRoundedMinutes(rawMinutes, totalMinutes);
  },

  hasPeakHoursConfig: (row) =>
    !_.isEmpty(ChargeTransactionHelper.parsePeakHoursConfig(row?.peakHoursConfig)),

  getExportChargingMinutes: (row) => {
    const emptyMinutes = {
      fixedMinutes: null,
      peakMinutes: null,
      normalMinutes: null,
      offPeakMinutes: null,
    };

    if (!ChargeTransactionHelper.hasPeakHoursConfig(row) || !row?.startTime || !row?.endTime) {
      return emptyMinutes;
    }

    const pricingMode = row?.actualPricing?.pricingMode;
    const peakHoursConfig = row?.peakHoursConfig;

    if (pricingMode === 'FLAT') {
      const start = moment(row.startTime);
      const end = moment(row.endTime);
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return emptyMinutes;
      }
      return {
        fixedMinutes: Math.round(end.diff(start, 'minutes', true)),
        peakMinutes: null,
        normalMinutes: null,
        offPeakMinutes: null,
      };
    }

    const minutesByConfig = ChargeTransactionHelper.calcChargingMinutesByPeakHoursConfig(
      row.startTime,
      row.endTime,
      peakHoursConfig
    );

    if (!minutesByConfig) {
      return emptyMinutes;
    }

    return {
      fixedMinutes: null,
      ...minutesByConfig,
    };
  },
};

export default ChargeTransactionHelper;
